import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Check, Headphones, Keyboard, Pause, Play, RotateCcw, Sparkles, X } from 'lucide-react'
import { createQuestion, EXERCISE_COPY } from '../domain/music'
import { createScaleContext, scaleDisplayName } from '../domain/pitchContext'
import type { ExerciseKind, Preferences, Question, QuestionAnswerParts, RoundResult, SessionAnswer, TrainingMode } from '../domain/types'
import { audioEngine } from '../lib/audio'
import { captureError, captureEvent } from '../lib/observability'

type SessionProps = {
  kind: ExerciseKind
  mode: TrainingMode
  preferences: Preferences
  paused?: boolean
  onComplete: (result: RoundResult) => void
  onExit: () => void
}

export function Session({ kind, mode, preferences, paused = false, onComplete, onExit }: SessionProps) {
  const makeQuestion = useCallback(() => createQuestion({
    kind,
    difficulty: preferences.difficulty,
    octaveRange: preferences.octaveRange,
    playbackStyle: preferences.playbackStyle,
    playbackSpeed: preferences.playbackSpeed,
    selectedScaleIds: preferences.selectedScaleIds,
    selectedProgressionIds: preferences.selectedProgressionIds,
    absolutePitchSource: preferences.absolutePitchSource,
    absolutePitchTonic: preferences.absolutePitchTonic,
    absolutePitchScaleId: preferences.absolutePitchScaleId,
  }), [kind, preferences.absolutePitchScaleId, preferences.absolutePitchSource, preferences.absolutePitchTonic, preferences.difficulty, preferences.octaveRange, preferences.playbackSpeed, preferences.playbackStyle, preferences.selectedProgressionIds, preferences.selectedScaleIds])

  const [question, setQuestion] = useState<Question>(makeQuestion)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<SessionAnswer[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [draftParts, setDraftParts] = useState<QuestionAnswerParts>({})
  const [submittedParts, setSubmittedParts] = useState<QuestionAnswerParts | null>(null)
  const [replayCount, setReplayCount] = useState(0)
  const [audioState, setAudioState] = useState<'idle' | 'playing' | 'error'>('idle')
  const [result, setResult] = useState<RoundResult | null>(null)
  const startedAt = useRef(Date.now())
  const pausedAt = useRef<number | null>(null)
  const playTimer = useRef<number | null>(null)

  const maxReplays = mode === 'scored' ? 2 : Number.POSITIVE_INFINITY
  const copy = EXERCISE_COPY[kind]
  const isMultipart = Boolean(question.choiceGroups && question.answerParts)
  const answered = selected !== null || submittedParts !== null
  const isAbsolutePitch = ['notes', 'chord-roots', 'chords', 'absolute-inversions'].includes(kind)
  const scaleContext = createScaleContext(preferences.absolutePitchTonic, preferences.absolutePitchScaleId)
  const progressPercent = result ? 100 : ((questionIndex + (answered ? 1 : 0)) / preferences.roundLength) * 100

  const playQuestion = useCallback(async () => {
    if (paused || audioState === 'playing' || replayCount >= maxReplays) return
    try {
      setAudioState('playing')
      await audioEngine.play(question.events, preferences.instrument)
      setReplayCount((count) => count + 1)
      const duration = Math.max(...question.events.map((event) => event.offset + event.duration), 0.2)
      if (playTimer.current) window.clearTimeout(playTimer.current)
      playTimer.current = window.setTimeout(() => setAudioState('idle'), duration * 1000 + 300)
      captureEvent('audio_play_succeeded', { kind, mode, instrument: preferences.instrument })
    } catch (error) {
      setAudioState('error')
      captureError(error, { kind, instrument: preferences.instrument })
      captureEvent('audio_play_failed', { kind, instrument: preferences.instrument })
    }
  }, [audioState, kind, maxReplays, mode, paused, preferences.instrument, question.events, replayCount])

  useEffect(() => {
    startedAt.current = Date.now()
    captureEvent('training_started', { kind, mode, difficulty: preferences.difficulty })
    return () => {
      audioEngine.stop()
      if (playTimer.current) window.clearTimeout(playTimer.current)
    }
  }, [kind, mode])

  useEffect(() => {
    if (paused) {
      pausedAt.current = Date.now()
      audioEngine.stop()
      if (playTimer.current) window.clearTimeout(playTimer.current)
      setAudioState('idle')
    } else if (pausedAt.current !== null) {
      startedAt.current += Date.now() - pausedAt.current
      pausedAt.current = null
    }
  }, [paused])

  const chooseAnswer = useCallback((answer: string) => {
    if (paused || answered || result) return
    const correct = answer === question.answer || question.acceptedAnswers?.includes(answer) === true
    const entry: SessionAnswer = {
      questionId: question.id,
      answer,
      correct,
      credit: correct ? 1 : 0,
      responseMs: Date.now() - startedAt.current,
      replayCount,
    }
    setSelected(answer)
    setAnswers((current) => [...current, entry])
    captureEvent('answer_submitted', {
      kind,
      mode,
      correct: entry.correct,
      credit: entry.credit,
      replayCount,
      responseMs: entry.responseMs,
    })
  }, [answered, kind, mode, paused, question.acceptedAnswers, question.answer, question.id, replayCount, result])

  const submitMultipartAnswer = useCallback(() => {
    if (paused || answered || result || !question.answerParts) return
    const parts = (['root', 'quality', 'inversion'] as const).filter((part) => question.answerParts?.[part] !== undefined)
    if (parts.some((part) => !draftParts[part])) return
    const partResults = Object.fromEntries(parts.map((part) => {
      const accepted = question.acceptedAnswerParts?.[part] ?? [question.answerParts?.[part] ?? '']
      return [part, accepted.includes(draftParts[part] ?? '')]
    })) as SessionAnswer['partResults']
    const correctParts = parts.filter((part) => partResults?.[part]).length
    const credit = correctParts / parts.length
    const entry: SessionAnswer = {
      questionId: question.id,
      answer: parts.map((part) => draftParts[part]).join(' · '),
      correct: credit === 1,
      credit,
      partResults,
      responseMs: Date.now() - startedAt.current,
      replayCount,
    }
    setSubmittedParts({ ...draftParts })
    setAnswers((current) => [...current, entry])
    captureEvent('answer_submitted', {
      kind,
      mode,
      correct: entry.correct,
      credit,
      replayCount,
      responseMs: entry.responseMs,
    })
  }, [answered, draftParts, kind, mode, paused, question.acceptedAnswerParts, question.answerParts, question.id, replayCount, result])

  const finishRound = useCallback((finalAnswers: SessionAnswer[]) => {
    const round: RoundResult = {
      id: crypto.randomUUID(),
      completedAt: new Date().toISOString(),
      kind,
      mode,
      difficulty: preferences.difficulty,
      correct: Math.round(finalAnswers.reduce((sum, answer) => sum + answer.credit, 0) * 100) / 100,
      total: finalAnswers.length,
      averageResponseMs: Math.round(finalAnswers.reduce((sum, answer) => sum + answer.responseMs, 0) / finalAnswers.length),
      questionSource: isAbsolutePitch ? preferences.absolutePitchSource : undefined,
      tonic: isAbsolutePitch && preferences.absolutePitchSource === 'scale' ? preferences.absolutePitchTonic : undefined,
      scaleId: isAbsolutePitch && preferences.absolutePitchSource === 'scale' ? preferences.absolutePitchScaleId : undefined,
    }
    setResult(round)
    onComplete(round)
    captureEvent('round_completed', {
      kind,
      mode,
      difficulty: preferences.difficulty,
      correct: round.correct,
      total: round.total,
      questionSource: round.questionSource ?? 'not-applicable',
    })
  }, [isAbsolutePitch, kind, mode, onComplete, preferences.absolutePitchScaleId, preferences.absolutePitchSource, preferences.absolutePitchTonic, preferences.difficulty])

  const nextQuestion = useCallback(() => {
    if (!answered) return
    if (questionIndex + 1 >= preferences.roundLength) {
      finishRound(answers)
      return
    }
    audioEngine.stop()
    setQuestion(makeQuestion())
    setQuestionIndex((index) => index + 1)
    setSelected(null)
    setDraftParts({})
    setSubmittedParts(null)
    setReplayCount(0)
    setAudioState('idle')
    startedAt.current = Date.now()
  }, [answered, answers, finishRound, makeQuestion, preferences.roundLength, questionIndex])

  const restart = useCallback(() => {
    setQuestion(makeQuestion())
    setQuestionIndex(0)
    setAnswers([])
    setSelected(null)
    setDraftParts({})
    setSubmittedParts(null)
    setReplayCount(0)
    setAudioState('idle')
    setResult(null)
    startedAt.current = Date.now()
  }, [makeQuestion])

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (paused || result) return
      if (event.code === 'Space') {
        event.preventDefault()
        void playQuestion()
      }
      const index = Number(event.key) - 1
      const usesPiano = (kind === 'notes' || kind === 'chord-roots') && preferences.answerInput === 'keyboard'
      if (!isMultipart && !usesPiano && !answered && index >= 0 && index < question.choices.length) chooseAnswer(question.choices[index])
      if (answered && event.key === 'Enter') nextQuestion()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [answered, chooseAnswer, isMultipart, kind, nextQuestion, paused, playQuestion, preferences.answerInput, question.choices, result])

  const score = useMemo(() => Math.round(answers.reduce((sum, answer) => sum + answer.credit, 0) * 100) / 100, [answers])
  const currentAnswer = answers[answers.length - 1]

  if (result) {
    const percentage = Math.round((result.correct / result.total) * 100)
    return (
      <main id={paused ? undefined : 'main-content'} className="session-shell result-shell">
        <div className="result-card">
          <div className="result-orbit"><Sparkles aria-hidden="true" /></div>
          <h1>Round Complete</h1>
          <div className="result-score"><strong>{percentage}%</strong><span>{formatPoints(result.correct)} of {result.total} points</span></div>
          <div className="result-grid">
            <div><span>Mode</span><strong>{mode === 'scored' ? 'Scored test' : 'Practice'}</strong></div>
            <div><span>Skill</span><strong>{copy.title}</strong></div>
            <div><span>Average response</span><strong>{(result.averageResponseMs / 1000).toFixed(1)}s</strong></div>
          </div>
          <p className="result-note">This round has been saved to Progress on this device.</p>
          <div className="result-actions">
            <button className="button button-primary button-large" type="button" onClick={restart}><RotateCcw aria-hidden="true" /> Train again</button>
            <button className="button button-quiet button-large" type="button" onClick={onExit}>Back to training</button>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main id={paused ? undefined : 'main-content'} className="session-shell">
      <div className="session-header">
        <button className="icon-text-button" type="button" onClick={onExit}><ArrowLeft aria-hidden="true" /> Exit round</button>
        <div className="session-title">
          <span>{copy.eyebrow}</span>
          <strong>{copy.title}</strong>
        </div>
        <div className="session-badges">
          <span>{mode === 'scored' ? 'Scored test' : 'Practice'}</span>
          <span>{preferences.difficulty}</span>
          {isAbsolutePitch && <span>{preferences.absolutePitchSource === 'scale' ? `${scaleContext.tonicName} ${scaleDisplayName(scaleContext.scale)}` : 'Random'}</span>}
        </div>
      </div>

      <div className="session-progress" aria-label={`Question ${questionIndex + 1} of ${preferences.roundLength}`}>
        <div className="session-progress-copy"><span>Question {questionIndex + 1} / {preferences.roundLength}</span><span>{formatPoints(score)} {score === 1 ? 'point' : 'points'}</span></div>
        <div className="progress-track"><span style={{ width: `${progressPercent}%` }} /></div>
      </div>

      <section className="question-stage" aria-live="polite">
        <div className="question-context">
          <span>{question.skill}</span>
          <p>{question.detail}</p>
        </div>

        <button
          className={audioState === 'playing' ? 'listen-button playing' : 'listen-button'}
          type="button"
          onClick={() => void playQuestion()}
          disabled={audioState === 'playing' || replayCount >= maxReplays}
          aria-label={audioState === 'playing' ? 'Audio is playing' : 'Play question audio'}
        >
          <span className="listen-rings" aria-hidden="true"><i /><i /><i /></span>
          {audioState === 'playing' ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
          <strong>{audioState === 'playing' ? 'Listen…' : replayCount ? 'Play again' : 'Play sound'}</strong>
          <small>{mode === 'scored' ? `${Math.max(0, maxReplays - replayCount)} play${maxReplays - replayCount === 1 ? '' : 's'} left` : 'Spacebar shortcut'}</small>
        </button>

        {audioState === 'error' && <p className="audio-error" role="alert">Audio could not start. Check your device volume and try again.</p>}

        <div className="answer-panel">
          <div className="answer-heading">
            <div><span className="eyebrow">Answer</span><h1>{question.prompt}</h1></div>
            <span className="keyboard-hint"><Keyboard aria-hidden="true" /> {isMultipart ? 'Complete every part' : (kind === 'notes' || kind === 'chord-roots') && preferences.answerInput === 'keyboard' ? 'Piano keyboard' : `Keys 1–${question.choices.length}`}</span>
          </div>
          {isMultipart && question.answerParts && question.choiceGroups ? (
            <MultipartAnswer
              answer={question.answerParts}
              accepted={question.acceptedAnswerParts}
              choices={question.choiceGroups}
              draft={draftParts}
              submitted={submittedParts}
              answerInput={preferences.answerInput}
              onChange={(part, value) => setDraftParts((current) => ({ ...current, [part]: value }))}
              onSubmit={submitMultipartAnswer}
            />
          ) : (kind === 'notes' || kind === 'chord-roots') && preferences.answerInput === 'keyboard' ? (
            <PianoAnswer selected={selected} answer={question.answer} accepted={question.acceptedAnswers} onChoose={chooseAnswer} />
          ) : (
            <div className="answer-grid">
              {question.choices.map((choice, index) => {
                const isSelected = selected === choice
                const accepted = [question.answer, ...(question.acceptedAnswers ?? [])]
                const isCorrect = selected !== null && accepted.includes(choice)
                const isWrong = isSelected && !accepted.includes(choice)
                const className = ['answer-button', isCorrect ? 'correct' : '', isWrong ? 'wrong' : '', selected && !isSelected && !isCorrect ? 'muted' : ''].filter(Boolean).join(' ')
                return (
                  <button className={className} type="button" key={choice} onClick={() => chooseAnswer(choice)} disabled={selected !== null}>
                    <span>{index + 1}</span><strong>{choice}</strong>
                    {isCorrect && <Check aria-label="Correct" />}
                    {isWrong && <X aria-label="Incorrect" />}
                  </button>
                )
              })}
            </div>
          )}

          {answered && currentAnswer && (
            <div className={currentAnswer.credit === 1 ? 'feedback-card success' : 'feedback-card retry'}>
              <div>
                <strong>{currentAnswer.credit === 1 ? 'That’s it.' : currentAnswer.credit > 0 ? `Partial credit: ${formatPoints(currentAnswer.credit)} / 1.` : `The answer was ${question.answer}.`}</strong>
                {submittedParts && question.answerParts && currentAnswer.partResults && (
                  <MultipartFeedback answer={question.answerParts} results={currentAnswer.partResults} />
                )}
                {mode === 'practice' && <p>{question.explanation}</p>}
                {mode === 'scored' && <p>Explanation is available in Practice mode so scored rounds remain comparable.</p>}
              </div>
              <button className="button button-primary" type="button" onClick={nextQuestion}>
                {questionIndex + 1 >= preferences.roundLength ? 'Finish round' : 'Next sound'} <ArrowLeft className="arrow-forward" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </section>
    </main>
  )
}

function formatPoints(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}

type AnswerPart = keyof QuestionAnswerParts

function MultipartAnswer({
  answer,
  accepted,
  choices,
  draft,
  submitted,
  answerInput,
  onChange,
  onSubmit,
}: {
  answer: QuestionAnswerParts
  accepted?: Question['acceptedAnswerParts']
  choices: NonNullable<Question['choiceGroups']>
  draft: QuestionAnswerParts
  submitted: QuestionAnswerParts | null
  answerInput: Preferences['answerInput']
  onChange: (part: AnswerPart, value: string) => void
  onSubmit: () => void
}) {
  const parts = (['root', 'quality', 'inversion'] as const).filter((part) => answer[part] !== undefined)
  const complete = parts.every((part) => Boolean(draft[part]))

  return (
    <div className="multipart-answer" aria-label="Chord answer parts">
      <fieldset className="answer-part">
        <legend><span>1</span> Root pitch <small>{answer.inversion ? 'one-third of question credit' : answer.quality ? '50% of question credit' : ''}</small></legend>
        {answerInput === 'keyboard' ? (
          <PianoPartSelector selected={draft.root ?? null} answer={answer.root ?? ''} accepted={accepted?.root} submitted={submitted !== null} onChoose={(value) => onChange('root', value)} />
        ) : (
          <PartChoiceGroup part="root" choices={choices.root ?? []} selected={draft.root} answer={answer.root} accepted={accepted?.root} submitted={submitted !== null} onChoose={(value) => onChange('root', value)} />
        )}
      </fieldset>

      {answer.quality && (
        <fieldset className="answer-part">
          <legend><span>2</span> Chord quality <small>{answer.inversion ? 'one-third of question credit' : '50% of question credit'}</small></legend>
          <PartChoiceGroup part="quality" choices={choices.quality ?? []} selected={draft.quality} answer={answer.quality} submitted={submitted !== null} onChoose={(value) => onChange('quality', value)} />
        </fieldset>
      )}

      {answer.inversion && (
        <fieldset className="answer-part">
          <legend><span>3</span> Inversion <small>one-third of question credit</small></legend>
          <PartChoiceGroup part="inversion" choices={choices.inversion ?? []} selected={draft.inversion} answer={answer.inversion} submitted={submitted !== null} onChoose={(value) => onChange('inversion', value)} />
        </fieldset>
      )}

      <button className="button button-primary multipart-submit" type="button" disabled={!complete || submitted !== null} onClick={onSubmit}>
        Submit complete answer
      </button>
    </div>
  )
}

function PartChoiceGroup({
  part,
  choices,
  selected,
  answer,
  accepted,
  submitted,
  onChoose,
}: {
  part: AnswerPart
  choices: string[]
  selected?: string
  answer?: string
  accepted?: string[]
  submitted: boolean
  onChoose: (value: string) => void
}) {
  return (
    <div className={`part-choice-grid ${part}`} role="group" aria-label={`Choose ${part}`}>
      {choices.map((choice) => {
        const chosen = selected === choice
        const isAccepted = (accepted ?? [answer ?? '']).includes(choice)
        const correct = submitted && isAccepted
        const wrong = submitted && chosen && !isAccepted
        const className = ['part-choice', chosen ? 'selected' : '', correct ? 'correct' : '', wrong ? 'wrong' : '', submitted && !chosen && !correct ? 'muted' : ''].filter(Boolean).join(' ')
        return (
          <button className={className} type="button" key={choice} disabled={submitted} aria-pressed={chosen} onClick={() => onChoose(choice)}>
            {choice}
            {correct && <Check aria-label="Correct" />}
            {wrong && <X aria-label="Incorrect" />}
          </button>
        )
      })}
    </div>
  )
}

function MultipartFeedback({ answer, results }: { answer: QuestionAnswerParts; results: NonNullable<SessionAnswer['partResults']> }) {
  const labels: Record<AnswerPart, string> = { root: 'Root', quality: 'Quality', inversion: 'Inversion' }
  const parts = (['root', 'quality', 'inversion'] as const).filter((part) => answer[part] !== undefined)
  return (
    <ul className="multipart-feedback" aria-label="Answer breakdown">
      {parts.map((part) => (
        <li className={results[part] ? 'correct' : 'wrong'} key={part}>
          {results[part] ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
          <span><strong>{labels[part]}:</strong> {results[part] ? 'correct' : answer[part]}</span>
        </li>
      ))}
    </ul>
  )
}

const whiteKeys = ['C', 'D', 'E', 'F', 'G', 'A', 'B']
const blackKeys = [
  { note: 'C♯', position: '14.285%' },
  { note: 'E♭', position: '28.57%' },
  { note: 'F♯', position: '57.14%' },
  { note: 'A♭', position: '71.425%' },
  { note: 'B♭', position: '85.71%' },
]

function PianoAnswer({ selected, answer, accepted, onChoose }: { selected: string | null; answer: string; accepted?: string[]; onChoose: (note: string) => void }) {
  const acceptedNotes = [answer, ...(accepted ?? [])]
  const keyClass = (note: string, color: 'white' | 'black') => [
    'piano-key',
    color,
    selected !== null && acceptedNotes.includes(note) ? 'correct' : '',
    selected === note && !acceptedNotes.includes(note) ? 'wrong' : '',
    selected !== null && selected !== note && !acceptedNotes.includes(note) ? 'muted' : '',
  ].filter(Boolean).join(' ')

  return (
    <div className="piano-answer" role="group" aria-label="Choose a note on the piano keyboard">
      <div className="piano-white-keys">
        {whiteKeys.map((note) => (
          <button className={keyClass(note, 'white')} type="button" key={note} onClick={() => onChoose(note)} disabled={selected !== null} aria-label={note}>
            <span>{note}</span>
          </button>
        ))}
      </div>
      {blackKeys.map(({ note, position }) => (
        <button className={keyClass(note, 'black')} style={{ left: position }} type="button" key={note} onClick={() => onChoose(note)} disabled={selected !== null} aria-label={note}>
          <span>{note}</span>
        </button>
      ))}
    </div>
  )
}

function PianoPartSelector({ selected, answer, accepted, submitted, onChoose }: { selected: string | null; answer: string; accepted?: string[]; submitted: boolean; onChoose: (note: string) => void }) {
  const acceptedNotes = accepted ?? [answer]
  const keyClass = (note: string, color: 'white' | 'black') => [
    'piano-key',
    color,
    !submitted && selected === note ? 'selected' : '',
    submitted && acceptedNotes.includes(note) ? 'correct' : '',
    submitted && selected === note && !acceptedNotes.includes(note) ? 'wrong' : '',
    submitted && selected !== note && !acceptedNotes.includes(note) ? 'muted' : '',
  ].filter(Boolean).join(' ')

  return (
    <div className="piano-answer multipart-piano" role="group" aria-label="Choose the chord root on the piano keyboard">
      <div className="piano-white-keys">
        {whiteKeys.map((note) => (
          <button className={keyClass(note, 'white')} type="button" key={note} onClick={() => onChoose(note)} disabled={submitted} aria-label={note} aria-pressed={selected === note}>
            <span>{note}</span>
          </button>
        ))}
      </div>
      {blackKeys.map(({ note, position }) => (
        <button className={keyClass(note, 'black')} style={{ left: position }} type="button" key={note} onClick={() => onChoose(note)} disabled={submitted} aria-label={note} aria-pressed={selected === note}>
          <span>{note}</span>
        </button>
      ))}
    </div>
  )
}
